/**
 * Lighthouse's voice: each sentence is POSTed to `/agent/v2/voice/speak` and the streamed raw PCM (s16le,
 * mono, 24 kHz per `X-Audio-Sample-Rate`) is played through the call's AudioContext as it arrives,
 * scheduled back to back so sentences join without gaps. The next sentence is fetched while one plays
 * (two requests in flight at most). An AnalyserNode gives the output level for the orb.
 *
 * 204 means nothing speakable. A retryable failure (503 `tts_busy`, a retryable 502) is retried once right
 * away, then that sentence falls back to device speech, as does a 429 `rate_limited` or a network error.
 * Out of quota / usage (429) or an older backend without the route (404) means device speech for the rest
 * of the call.
 */
import type { TtsEngine, TtsHandlers } from '../types'
import { dbToLevel } from '../audio/context'
import { backendUrl, voiceHeaders } from '../http'
import { PcmDecoder, SampleBlocks } from './pcm'
import { createDeviceSpeech, type DeviceSpeech } from './fallback'

export interface SpeakBody {
    text: string
    format: 'pcm'
    voice?: string
    speed?: number
    session_id?: string
}

export interface StreamTtsOptions {
    context: () => AudioContext | null
    sessionId?: () => string | null
    voice?: string
    speed?: number
    language?: () => string
    fetchSpeech?: (body: SpeakBody, signal: AbortSignal) => Promise<Response>
    device?: DeviceSpeech
}

interface Item {
    text: string
    runId: string | null
    state: 'queued' | 'loading' | 'loaded'
    device: boolean
    blocks: Float32Array[]
    done: boolean
    ctrl: AbortController | null
    sampleRate: number
    started: boolean
}

async function defaultFetch(body: SpeakBody, signal: AbortSignal): Promise<Response> {
    return fetch(backendUrl('/agent/v2/voice/speak'), {
        method: 'POST',
        headers: await voiceHeaders({ 'Content-Type': 'application/json', Accept: 'audio/pcm' }),
        body: JSON.stringify(body),
        signal,
    })
}

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms))

/** The harness error body: `{ error: { code, message, details: { retryable } } }`. */
async function errorOf(res: Response): Promise<{ code: string; retryable: boolean }> {
    try {
        const body = (await res.json()) as { error?: { code?: string; details?: { retryable?: boolean } } }
        return { code: body?.error?.code ?? '', retryable: Boolean(body?.error?.details?.retryable) }
    } catch {
        return { code: '', retryable: res.status === 503 }
    }
}

export function createStreamTts(h: TtsHandlers, opts: StreamTtsOptions): TtsEngine {
    const fetchSpeech = opts.fetchSpeech ?? defaultFetch
    const device = opts.device ?? createDeviceSpeech()
    const language = opts.language ?? (() => navigator.language || 'en-US')
    /** Items waiting to be scheduled; items[0] is the one being scheduled now. */
    let items: Item[] = []
    /** Sources scheduled and not yet ended (any item). */
    const live = new Set<AudioBufferSourceNode>()
    let nextStart = 0
    let held = false
    let endpointGone = false
    let deviceBusy = false
    let gain: GainNode | null = null
    let analyser: AnalyserNode | null = null
    let meterBuf: Float32Array<ArrayBuffer> | null = null
    let timers: ReturnType<typeof setTimeout>[] = []
    let generation = 0

    function graph(ctx: AudioContext): GainNode {
        if (gain && gain.context === ctx) return gain
        gain = ctx.createGain()
        analyser = ctx.createAnalyser()
        analyser.fftSize = 512
        meterBuf = new Float32Array(analyser.fftSize)
        gain.connect(analyser)
        analyser.connect(ctx.destination)
        return gain
    }

    function pump() {
        for (const it of items.slice(0, 2)) if (it.state === 'queued') void load(it, generation)
        schedule()
    }

    async function load(it: Item, gen: number) {
        const ctx = opts.context()
        if (endpointGone || !ctx) {
            Object.assign(it, { device: true, done: true, state: 'loaded' })
            schedule()
            return
        }
        it.state = 'loading'
        it.ctrl = new AbortController()
        const sid = opts.sessionId?.() ?? null
        const body: SpeakBody = { text: it.text, format: 'pcm', ...(opts.voice ? { voice: opts.voice } : {}), ...(opts.speed ? { speed: opts.speed } : {}), ...(sid ? { session_id: sid } : {}) }
        h.requestStart?.(it.text)
        for (let attempt = 0; ; attempt++) {
            try {
                const res = await fetchSpeech(body, it.ctrl.signal)
                if (gen !== generation) return
                if (res.status === 204) break
                if (res.status === 404 || res.status === 405 || res.status === 501) {
                    endpointGone = true
                    it.device = true
                    break
                }
                if (!res.ok) {
                    const err = await errorOf(res)
                    if (gen !== generation) return
                    if (err.code === 'quota_exceeded' || err.code === 'usage_exhausted') endpointGone = true
                    else if ((res.status === 503 || (res.status === 502 && err.retryable)) && attempt < 1) {
                        await delay(350)
                        if (gen !== generation) return
                        continue
                    }
                    it.device = true
                    break
                }
                if (!res.body) {
                    it.device = true
                    break
                }
                it.sampleRate = Number(res.headers.get('X-Audio-Sample-Rate')) || 24_000
                const decoder = new PcmDecoder()
                const blocks = new SampleBlocks(Math.round(it.sampleRate * 0.12), Math.round(it.sampleRate * 0.3))
                const reader = res.body.getReader()
                for (;;) {
                    const { value, done } = await reader.read()
                    if (gen !== generation) return
                    if (done) break
                    const ready = blocks.push(decoder.push(value))
                    if (ready.length) {
                        if (!it.started && !it.blocks.length) h.firstAudio?.(it.text)
                        it.blocks.push(...ready)
                        schedule()
                    }
                }
                it.blocks.push(...blocks.end())
                break
            } catch {
                if (gen !== generation || it.ctrl?.signal.aborted) return
                // Half a sentence already played: keep it rather than repeat it in another voice.
                if (!it.started && !it.blocks.length) it.device = true
                break
            }
        }
        it.done = true
        it.state = 'loaded'
        schedule()
    }

    function at(ctx: AudioContext, when: number, fn: () => void) {
        const t = setTimeout(fn, Math.max(0, (when - ctx.currentTime) * 1000))
        timers.push(t)
    }

    function schedule() {
        if (held) return
        const it = items[0]
        if (!it) {
            maybeDrained()
            return
        }
        if (it.device) {
            if (it.done && !deviceBusy && live.size === 0) void speakOnDevice(it, generation)
            return
        }
        const ctx = opts.context()
        if (!ctx) return
        const out = graph(ctx)
        while (it.blocks.length) {
            const block = it.blocks.shift()!
            const buf = ctx.createBuffer(1, block.length, it.sampleRate)
            buf.getChannelData(0).set(block)
            const src = ctx.createBufferSource()
            src.buffer = buf
            src.connect(out)
            const when = Math.max(ctx.currentTime + 0.04, nextStart)
            src.start(when)
            nextStart = when + buf.duration
            live.add(src)
            src.onended = () => {
                live.delete(src)
                schedule()
            }
            if (!it.started) {
                it.started = true
                const text = it.text
                at(ctx, when, () => h.chunkStart(text))
            }
        }
        if (it.done) {
            // Fully scheduled: move on so the next sentence lines up right behind it.
            items.shift()
            pump()
        }
    }

    async function speakOnDevice(it: Item, gen: number) {
        deviceBusy = true
        h.chunkStart(it.text)
        await device.speak(it.text, language())
        deviceBusy = false
        if (gen !== generation) return
        if (items[0] === it) items.shift()
        pump()
    }

    function maybeDrained() {
        if (!items.length && !live.size && !deviceBusy) h.drained()
    }

    function stopAll() {
        generation += 1
        for (const t of timers) clearTimeout(t)
        timers = []
        for (const it of items) it.ctrl?.abort()
        items = []
        const ctx = opts.context()
        const g = gain
        if (ctx && g) {
            // A short fade, then a fresh gain for whatever plays next.
            try {
                g.gain.cancelScheduledValues(ctx.currentTime)
                g.gain.setValueAtTime(g.gain.value, ctx.currentTime)
                g.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.06)
            } catch {
                /* closed context */
            }
            const sources = [...live]
            setTimeout(() => {
                for (const s of sources) {
                    try {
                        s.onended = null
                        s.stop()
                    } catch {
                        /* already stopped */
                    }
                }
                try {
                    g.disconnect()
                } catch {
                    /* gone */
                }
            }, 70)
            gain = null
            analyser = null
        }
        live.clear()
        nextStart = 0
        if (deviceBusy || device.speaking) device.stop()
        deviceBusy = false
    }

    return {
        kind: 'stream',
        enqueue(text, meta) {
            const t = text.trim()
            if (!t) return
            const ctx = opts.context()
            if (ctx && ctx.state !== 'running') void ctx.resume().catch(() => {})
            items.push({ text: t, runId: meta?.runId ?? null, state: 'queued', device: false, blocks: [], done: false, ctrl: null, sampleRate: 24_000, started: false })
            pump()
        },
        stop() {
            stopAll()
        },
        hold(on) {
            held = on
            if (!on) schedule()
        },
        level() {
            if (deviceBusy) {
                const t = performance.now() / 1000
                return 0.35 + 0.2 * Math.sin(t * 9) * Math.sin(t * 2.3)
            }
            if (!analyser || !meterBuf || !live.size) return 0
            analyser.getFloatTimeDomainData(meterBuf)
            let sum = 0
            for (let i = 0; i < meterBuf.length; i++) sum += meterBuf[i] * meterBuf[i]
            const db = 20 * Math.log10(Math.sqrt(sum / meterBuf.length) || 1e-9)
            return dbToLevel(db)
        },
        get busy() {
            return items.length > 0 || live.size > 0 || deviceBusy
        },
        dispose() {
            stopAll()
        },
    }
}
